import React, { useMemo } from 'react';
import SearchableSelect from '../SearchableSelect';

const TableFiltersConfiguration = ({ block, onUpdateFilters, dataContext, extractDataObj }) => {
    // Support both old format (plain array) and new format ({ filters: [...], combinator: 'AND' })
    const rawTableFilters = block.dataConfig?.tableFilters;
    const tableFilters = Array.isArray(rawTableFilters) ? rawTableFilters : (rawTableFilters?.filters || []);
    const combinator = rawTableFilters?.combinator || 'AND';
    
    // 1. Get the parsed row data to determine columns and values.
    const rawRows = useMemo(() => {
        if (!extractDataObj || typeof extractDataObj.extractor !== 'function') return [];
        try {
            return extractDataObj.extractor(dataContext, block.dataConfig) || [];
        } catch (err) {
            console.error('[TableFiltersConfiguration] Error extracting data', err);
            return [];
        }
    }, [extractDataObj, dataContext, block.dataConfig]);

    // 2. Identify available columns for this widget
    const availableFields = useMemo(() => {
        let columns = [];
        if (extractDataObj && extractDataObj.availableColumns) {
            columns = extractDataObj.availableColumns;
        } else if (rawRows.length > 0) {
            columns = Object.keys(rawRows[0]);
        }
        
        return columns.map(col => ({
            label: col,
            value: col,
            path: col
        }));
    }, [rawRows, extractDataObj]);

    // 3. Simple Operators
    const operators = [
        { value: 'equals', label: 'Equals' },
        { value: 'not_equals', label: 'Not Equals' },
        { value: 'contains', label: 'Contains' },
        { value: 'not_contains', label: 'Not Contains' },
        { value: 'starts_with', label: 'Starts With' },
        { value: 'ends_with', label: 'Ends With' },
        { value: 'is_empty', label: 'Is Empty/Null' },
        { value: 'is_not_empty', label: 'Is Not Empty' }
    ];

    const operatorNeedsValue = (operator) => {
        return !['is_empty', 'is_not_empty'].includes(operator);
    };

    // Helper to emit filter updates in the new { filters, combinator } shape
    const emitUpdate = (newFilters, newCombinator) => {
        onUpdateFilters({
            filters: newFilters,
            combinator: newCombinator || combinator
        });
    };

    const addFilter = () => {
        const newFilter = {
            id: `filter-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            fieldPath: availableFields.length > 0 ? availableFields[0].value : '',
            operator: 'contains',
            value: ''
        };
        emitUpdate([...tableFilters, newFilter]);
    };

    const removeFilter = (filterId) => {
        emitUpdate(tableFilters.filter(f => f.id !== filterId));
    };

    const updateFilter = (filterId, updates) => {
        emitUpdate(tableFilters.map(f =>
            f.id === filterId ? { ...f, ...updates } : f
        ));
    };

    const toggleCombinator = () => {
        emitUpdate(tableFilters, combinator === 'AND' ? 'OR' : 'AND');
    };

    // 4. Get exact values from the parsed rows to provide autocomplete suggestions
    const getSampleValuesForField = (fieldPath) => {
        if (!fieldPath || !rawRows.length) return [];
        const values = new Set();
        rawRows.forEach(row => {
            const val = row[fieldPath];
            if (val !== undefined && val !== null && val !== '') {
                values.add(String(val));
            }
        });
        return Array.from(values).sort().slice(0, 50);
    };

    return (
        <div className="device-filters-config" style={{ marginTop: '1rem' }}>
            <div className="property-field" style={{ borderTop: '1px solid var(--border)', paddingTop: '1rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 'bold' }}>
                    🔍 Table Column Filters
                </label>
                <small className="help-text" style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>
                    Filter rows displayed in this table based on the actual column data.
                </small>

                {tableFilters.length === 0 && (
                    <div style={{
                        padding: '0.75rem', textAlign: 'center', color: 'var(--text-secondary)',
                        border: '1px dashed var(--border)', borderRadius: 'var(--radius)',
                        marginBottom: '0.5rem', fontSize: '0.875rem'
                    }}>
                        No table filters configured.
                    </div>
                )}

                {tableFilters.map((filter, index) => (
                    <React.Fragment key={filter.id}>
                        {/* AND/OR combinator between filters */}
                        {index > 0 && (
                            <div style={{
                                display: 'flex', justifyContent: 'center', alignItems: 'center',
                                margin: '0.25rem 0', gap: '0.5rem'
                            }}>
                                <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
                                <button type="button" onClick={toggleCombinator}
                                    style={{
                                        padding: '0.15rem 0.75rem',
                                        fontSize: '0.75rem',
                                        fontWeight: '700',
                                        border: '1px solid var(--border)',
                                        borderRadius: '999px',
                                        background: combinator === 'AND' ? '#e0f2fe' : '#fef3c7',
                                        color: combinator === 'AND' ? '#0369a1' : '#92400e',
                                        cursor: 'pointer',
                                        letterSpacing: '0.05em',
                                        lineHeight: '1.5'
                                    }}
                                    title={`Click to switch to ${combinator === 'AND' ? 'OR' : 'AND'}`}
                                >
                                    {combinator}
                                </button>
                                <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
                            </div>
                        )}

                        <div style={{
                            marginBottom: '0.25rem', padding: '0.75rem',
                            border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                            backgroundColor: 'var(--background)'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                <strong style={{ fontSize: '0.875rem' }}>Filter {index + 1}</strong>
                                <button type="button" onClick={() => removeFilter(filter.id)}
                                    style={{ background: 'none', border: 'none', color: 'var(--error)', cursor: 'pointer', fontSize: '1.25rem', padding: '0', lineHeight: '1' }}
                                    title="Remove filter">×</button>
                            </div>

                            {/* Column Field */}
                            <div style={{ marginBottom: '0.5rem' }}>
                                <SearchableSelect 
                                    value={filter.fieldPath || ''}
                                    onChange={(newPath) => updateFilter(filter.id, { fieldPath: newPath })}
                                    options={availableFields}
                                    placeholder="Select column..."
                                />
                            </div>

                            {/* Operator */}
                            <div style={{ marginBottom: '0.5rem' }}>
                                <select value={filter.operator || 'contains'} onChange={(e) => updateFilter(filter.id, { operator: e.target.value })}
                                    style={{ width: '100%', padding: '0.5rem', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: '0.875rem' }}>
                                    {operators.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
                                </select>
                            </div>

                            {/* Value with Suggestions from Parsed Data */}
                            {operatorNeedsValue(filter.operator) && (
                                <div>
                                    <input type="text" value={filter.value || ''}
                                        onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
                                        placeholder="Enter value..."
                                        list={`table-filter-values-${filter.id}`}
                                        style={{ width: '100%', padding: '0.5rem', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: '0.875rem' }} />
                                    {filter.fieldPath && (
                                        <datalist id={`table-filter-values-${filter.id}`}>
                                            {getSampleValuesForField(filter.fieldPath).map((val, idx) => (
                                                <option key={idx} value={val} />
                                            ))}
                                        </datalist>
                                    )}
                                </div>
                            )}
                        </div>
                    </React.Fragment>
                ))}

                <button type="button" onClick={addFilter}
                    style={{
                        width: '100%', padding: '0.5rem', background: 'var(--primary)', color: 'white',
                        border: 'none', borderRadius: 'var(--radius)', cursor: 'pointer',
                        fontSize: '0.875rem', fontWeight: '500', marginTop: '0.5rem'
                    }}>
                    + Add Column Filter
                </button>
            </div>
        </div>
    );
};

export default TableFiltersConfiguration;
